import { create } from 'zustand';
import { registry } from '../core/Registry.js';

// Zufällige ID für neue Nodes generieren
const generateId = () => 'wdg_' + Math.random().toString(36).substr(2, 9);

// Echter Elementor-Workflow: Wir verwalten den Widget-Baum, das aktuelle Element und Undo/Redo
export const useDesignerStore = create((set, get) => ({
  // --- STATE ---
  isReady: false,
  deviceMode: 'desktop', // desktop, tablet, mobile
  activeTab: 'content',  // content, style, advanced (für den Inspector)
  
  // Der gesamte JSON-Baum (die Seite)
  pageData: {
    id: null,
    title: 'Neue Seite',
    slug: '',
    status: 'draft',
    content: [] // Array of root sections
  },
  
  // Welche Node ist gerade angewählt (wird im Inspector angezeigt)
  selectedNodeId: null,
  
  // Drag & Drop Status
  isDragging: false,
  draggedWidgetType: null,

  // Clipboard (für Strg+C / Strg+V)
  clipboardNode: null,
  
  // Saving Status
  isSaving: false,

  // --- ACTIONS ---
  setDeviceMode: (mode) => set({ deviceMode: mode }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  setSelectedNodeId: (id) => set({ selectedNodeId: id }),
  setDragging: (isDragging, type = null) => set({ isDragging, draggedWidgetType: type }),
  setPageData: (updates) => set((state) => ({ pageData: { ...state.pageData, ...updates } })),
  
  // Wird später die API-Ladedaten injizieren
  initPage: (pageData) => set({ 
    pageData, 
    isReady: true,
    selectedNodeId: null
  }),

  // Helpers für den Tree
  getSelectedNode: () => {
    const { pageData, selectedNodeId } = get();
    if (!selectedNodeId) return null;
    
    // Einfache rekursive Suche im Baum
    let found = null;
    const searchNode = (nodes) => {
      for (let node of nodes) {
        if (node.id === selectedNodeId) {
          found = node;
          return;
        }
        if (node.children && node.children.length > 0) {
          searchNode(node.children);
        }
      }
    };
    searchNode(pageData.content);
    return found;
  },

  // === TREE MANIPULATION (Drag & Drop) ===
  
  // Fügt einen neuen Node (aus der Sidebar) in den Baum ein
  addNode: (type, parentId = null, index = -1) => {
    const config = registry.getWidget(type);
    if (!config) return null;

    // Erzeuge ein neues Instanz-Objekt basierend auf registry defaultProps
    const newNode = {
      id: generateId(),
      type: type,
      content: config.defaultProps?.content ? JSON.parse(JSON.stringify(config.defaultProps.content)) : {},
      style: config.defaultProps?.style ? JSON.parse(JSON.stringify(config.defaultProps.style)) : {},
      advanced: config.defaultProps?.advanced ? JSON.parse(JSON.stringify(config.defaultProps.advanced)) : {},
      children: config.category === 'layout' ? [] : undefined // Nur Layouts haben Children
    };

    set((state) => {
      const newPageData = { ...state.pageData };
      newPageData.content = JSON.parse(JSON.stringify(state.pageData.content)); // Deep Clone

      // Root Level Drop
      if (!parentId) {
        if (index === -1) {
          newPageData.content.push(newNode);
        } else {
          newPageData.content.splice(index, 0, newNode);
        }
      } else {
        // Nested Drop
        let foundParent = null;
        const findParent = (nodes) => {
          for (let node of nodes) {
            if (node.id === parentId) {
              foundParent = node;
              return;
            }
            if (node.children) findParent(node.children);
          }
        };
        findParent(newPageData.content);

        if (foundParent && foundParent.children) {
          if (index === -1) {
            foundParent.children.push(newNode);
          } else {
            foundParent.children.splice(index, 0, newNode);
          }
        }
      }

      return { 
        pageData: newPageData,
        selectedNodeId: newNode.id // Wähle das neue Element direkt aus
      };
    });
    
    return newNode.id;
  },

  // Löscht einen Node aus dem Baum
  removeNode: (nodeId) => {
    set((state) => {
      const newPageData = JSON.parse(JSON.stringify(state.pageData));
      
      const removeRecursive = (nodes) => {
        const index = nodes.findIndex(n => n.id === nodeId);
        if (index !== -1) {
          nodes.splice(index, 1);
          return true;
        }
        for (let node of nodes) {
          if (node.children && removeRecursive(node.children)) return true;
        }
        return false;
      };
      
      removeRecursive(newPageData.content);
      
      return { 
        pageData: newPageData,
        selectedNodeId: state.selectedNodeId === nodeId ? null : state.selectedNodeId
      };
    });
  },

  // Aktualisiert Eigenschaften eines Nodes (z.B. "style.desktop.color": "#fff")
  updateNode: (nodeId, fieldPath, value) => {
    set((state) => {
      const newPageData = JSON.parse(JSON.stringify(state.pageData));
      
      let foundNode = null;
      const findNode = (nodes) => {
        for (let node of nodes) {
          if (node.id === nodeId) {
            foundNode = node;
            return;
          }
          if (node.children) findNode(node.children);
        }
      };
      findNode(newPageData.content);

      if (foundNode) {
        const parts = fieldPath.split('.');
        let current = foundNode;
        // Traversiere durch das Objekt anhand des Pfades (z.B. style -> desktop -> color)
        for (let i = 0; i < parts.length - 1; i++) {
          if (current[parts[i]] === undefined) {
            current[parts[i]] = {}; // Falls der Knoten noch nicht existiert (z.B. mobile style)
          }
          current = current[parts[i]];
        }
        // Setze den endgültigen Wert
        current[parts[parts.length - 1]] = value;
      }
      
      return { pageData: newPageData };
    });
  },

  // === KEYBOARD SHORTCUTS (Copy & Paste) ===
  copyNode: () => {
    const { selectedNodeId, getSelectedNode } = get();
    if (!selectedNodeId) return;
    
    const nodeToCopy = getSelectedNode();
    if (nodeToCopy) {
      // Ein sauberes Deep-Clone des Knotens erstellen
      set({ clipboardNode: JSON.parse(JSON.stringify(nodeToCopy)) });
    }
  },

  pasteNode: () => {
    const { clipboardNode, selectedNodeId, getSelectedNode } = get();
    if (!clipboardNode) return;

    set((state) => {
      const newPageData = JSON.parse(JSON.stringify(state.pageData));
      
      // Helper: Generiere rekursiv neue IDs für den geklonten Baum
      const regenerateIds = (node) => {
        node.id = generateId();
        if (node.children) {
          node.children.forEach(regenerateIds);
        }
        return node;
      };

      const clonedNode = regenerateIds(JSON.parse(JSON.stringify(clipboardNode)));

      // Wenn nichts ausgewählt ist, füge es ans Ende des Roots an
      if (!selectedNodeId) {
        newPageData.content.push(clonedNode);
        return { pageData: newPageData, selectedNodeId: clonedNode.id };
      }

      // Versuche das ausgewählte Element zu finden
      let targetNode = null;
      let targetParentArray = null;
      let targetIndex = -1;

      const findContext = (nodes) => {
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          if (node.id === selectedNodeId) {
            targetNode = node;
            targetParentArray = nodes;
            targetIndex = i;
            return true;
          }
          if (node.children && findContext(node.children)) return true;
        }
        return false;
      };
      
      findContext(newPageData.content);

      if (targetParentArray) {
        // Falls das ausgewählte Element ein Container (Layout) ist, fügen wir es als Kind ein
        const isLayout = registry.getWidget(targetNode.type)?.category === 'layout';
        
        if (isLayout && targetNode.children) {
          targetNode.children.push(clonedNode);
        } else {
          // Ansonsten direkt *unter* dem aktuell ausgewählten Element (als Sibling) einfügen
          targetParentArray.splice(targetIndex + 1, 0, clonedNode);
        }
      } else {
        // Fallback: Ins Root anfügen
        newPageData.content.push(clonedNode);
      }

      return { pageData: newPageData, selectedNodeId: clonedNode.id };
    });
  },

  // === API / BACKEND CALLS ===
  savePage: async () => {
    const { pageData } = get();
    set({ isSaving: true });
    
    // Prüfe ob dies eine Page oder ein Template ist
    const isTemplate = pageData.type === 'template';
    const endpoint = isTemplate ? '/api/admin/designer/templates' : '/api/admin/designer/pages';
    
    // Baue passendes Payload abhängig vom Typ
    const payload = {
      id: pageData.id && pageData.id !== 'dummy-1' ? pageData.id : null, 
      content: pageData.content,
    };
    
    if (isTemplate) {
      payload.name = pageData.title;
      payload.type = 'section'; // Typ des Templates
      payload.preview_img = '';
    } else {
      payload.title = pageData.title;
      payload.slug = pageData.slug || '';
      payload.status = pageData.status || 'draft';
      payload.page_type = pageData.type || 'page';
    }

    try {
      const token = localStorage.getItem('tef_jwt');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.ok) {
        set({ isSaving: false, pageData: { ...pageData, id: data.id } });
        // Eventuelles externes Skript benachrichtigen oder kurz Feedback geben
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      alert('Fehler beim Speichern: ' + err.message);
      set({ isSaving: false });
    }
  }

}));
